import { CSRF_HEADER } from '@orgus/protocol';
import type {
  ActionKind,
  ActionParamsMap,
  ActionResponse,
  ApiErrorBody,
  LogRef,
  UiErrorShape,
} from '@orgus/protocol';

import { ApiError, isUiErrorShape, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { absentError, moduleAbsent, plainHostedRefusal, moduleForPath, serverInfo } from '../features/server/server.ts';

export { ApiError, toUiError };
export type { UiError };

const STATUS_ERRORS: Record<number, Omit<UiError, 'status'>> = {
  401: {
    what: 'You are not signed in to this hub.',
    why: 'Your session cookie is missing or has expired.',
    next: 'Open the sign-in link the hub printed when it started (it ends in #token=).',
  },
  403: {
    what: 'The hub refused this request.',
    why: 'The request did not carry the Orgus header, or your session cannot do this.',
    next: 'Reload the page and try again.',
  },
  404: {
    what: 'The hub does not know that address.',
    why: 'The hub may be an older version than this page.',
    next: 'This page may be newer than the hub. Restart the hub (orgus up) so both run the same version, then reload.',
  },
  502: {
    what: 'Could not reach the hub.',
    why: 'Nothing answered at the hub address.',
    next: 'Start it with orgus up (or npm run dev), then try again.',
  },
  503: {
    what: 'The hub is not ready.',
    why: 'It is starting up or shutting down.',
    next: 'Wait a few seconds, then try again.',
  },
  504: {
    what: 'The machine did not answer in time.',
    why: 'It may be busy, asleep, or offline.',
    next: 'Check Machines, then try again.',
  },
};

export interface ApiInit {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  signal?: AbortSignal;
  /** Default 35 s (actions wait up to 30 s for the machine). */
  timeoutMs?: number;
}

/** Base URL of the hub API; same origin in production and through the Vite proxy in dev. */
export const API_BASE = '';

/** fetch wrapper: JSON in and out, the CSRF header on every request, errors normalised to ApiError. */
export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const { method = 'GET', body, signal, timeoutMs = 35_000 } = init;
  // Never ask a server for a module it says it does not run (Orgus Cloud lists its modules).
  const module = moduleForPath(path);
  const server = await serverInfo();
  if (moduleAbsent(server, path)) throw absentError(module, path);
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onAbort = () => ctrl.abort();
  signal?.addEventListener('abort', onAbort, { once: true });
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        [CSRF_HEADER]: '1',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: ctrl.signal,
    });
  } catch (e) {
    if (signal?.aborted) throw e;
    throw new ApiError({
      what: 'Could not reach the hub.',
      why: ctrl.signal.aborted ? `It did not answer within ${Math.round(timeoutMs / 1000)} s.` : 'The connection failed before it answered.',
      next: 'Check that Orgus is running (orgus up), then try again.',
    });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }

  const text = await res.text();
  let json: unknown = undefined;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = undefined;
    }
  }
  if (!res.ok) {
    // On Orgus Cloud, an address it does not know is a feature not ported yet, not a failure.
    if (res.status === 404 && server.kind === 'cloud' && method === 'GET' && /does not know that address|is not part of/.test(JSON.stringify(json ?? ''))) throw absentError(module, path);
    const fromBody = (json as Partial<ApiErrorBody> | undefined)?.error;
    if (isUiErrorShape(fromBody) && fromBody.code === 'not-on-control-plane') throw new ApiError({ ...plainHostedRefusal(fromBody), status: res.status }, res.status);
    if (isUiErrorShape(fromBody)) throw new ApiError({ ...fromBody, next: fromBody.next || 'Try again.', status: res.status }, res.status);
    const known = STATUS_ERRORS[res.status];
    throw new ApiError(
      known
        ? { ...known, status: res.status }
        : {
            what: 'The hub could not complete that.',
            why: `It answered with status ${res.status}.`,
            next: 'Try again. If it keeps failing, check the hub terminal for details.',
            status: res.status,
          },
      res.status,
    );
  }
  return json as T;
}

export const apiGet = <T>(path: string, signal?: AbortSignal): Promise<T> => api<T>(path, { signal });
export const apiPost = <T>(path: string, body?: unknown): Promise<T> => api<T>(path, { method: 'POST', body: body ?? {} });
export const apiDelete = <T>(path: string): Promise<T> => api<T>(path, { method: 'DELETE' });

/** Mock mode installs a handler so actions work without a hub. */
type ActionHandler = <K extends ActionKind>(machineId: string, kind: K, params: ActionParamsMap[K]) => Promise<ActionResponse>;
let actionHandler: ActionHandler | null = null;
export function setActionHandler(h: ActionHandler | null): void {
  actionHandler = h;
}

/**
 * Run an action on a machine (POST /api/actions). Resolves with the response when ok;
 * throws ApiError with the machine's message when it refused or failed.
 */
export async function runAction<K extends ActionKind>(machineId: string, kind: K, params: ActionParamsMap[K]): Promise<ActionResponse> {
  const res = actionHandler
    ? await actionHandler(machineId, kind, params)
    : await apiPost<ActionResponse>('/api/actions', { machineId, kind, params });
  if (!res.ok) {
    throw new ApiError({
      what: 'The machine refused that.',
      why: res.message ?? 'It did not say why.',
      next: 'Check the details and try again.',
    });
  }
  return res;
}

/**
 * Identifies this tab to the hub (on the SSE stream and on every log it opens), so log
 * chunks reach only the tab that asked for them and the hub closes them when it leaves.
 */
export const CLIENT_ID = `tab-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;

/** Open a log stream; chunks arrive as SSE `log` events and land in the store under the returned streamId. */
export function openLog(ref: LogRef): Promise<{ streamId: string }> {
  return apiPost<{ streamId: string }>(`/api/logs/${encodeURIComponent(`${ref.type}:${ref.id}`)}`, { clientId: CLIENT_ID });
}

/** Close a log stream on the hub, which stops the machine tailing it. Never throws. */
export function closeLog(streamId: string): Promise<void> {
  return apiDelete(`/api/logs/${encodeURIComponent(streamId)}`).then(
    () => undefined,
    () => undefined,
  );
}

/**
 * If the page was opened with #token=..., exchange it for the HttpOnly cookie
 * (POST /api/session) and strip it from the address bar.
 */
export async function bootstrapAuth(): Promise<void> {
  const m = /(?:^|[#&])token=([^&]+)/.exec(window.location.hash);
  if (!m?.[1]) return;
  const token = decodeURIComponent(m[1]);
  const url = new URL(window.location.href);
  url.hash = '';
  window.history.replaceState(null, '', url.toString());
  await apiPost('/api/session', { token });
}
